// Shared URL-sync logic for list views that keep their active tab (route hash) and filters
// (route query.filters, JSON-encoded) in sync with the current filters/tab state.
// Consuming components must provide:
//   - a `filters` data property (deep-watched by the component itself)
//   - an `initialFilters()` method returning the default filters shape
//   - a `defaultTab` data property (or computed) used as the fallback tab when the URL has no hash
export default {
  methods: {
    // Tab to select on mount: current URL hash, or the given default tab when there is none.
    // Callers that use this in data() must pass the default explicitly, because sibling data
    // properties (this.defaultTab) are not yet on the instance while data() is evaluated;
    // it falls back to this.defaultTab for any call made after initialization.
    initialHash(defaultTab = this.defaultTab) {
      const queryHash = this.$router.currentRoute.hash.slice(1);
      return queryHash ? queryHash : defaultTab;
    },
    // Single source of truth for writing the tab hash (+ current filters) into the URL, used by both
    // manual tab clicks (@change on v-tabs) and goToTab, so neither can clobber the other's write.
    // Prune a filters object to only the values that differ from the empty/default (empty arrays,
    // null, false and '' are dropped), so the URL carries just the active filters instead of the
    // whole shape. initialFilters() merges the result back over the defaults on read, so a compact
    // — or entirely missing — `filters` param still restores the full shape.
    pruneFilters(filters) {
      const compact = {};
      Object.entries(filters || {}).forEach(([key, value]) => {
        if (value === null || value === undefined || value === false || value === '') return;
        if (Array.isArray(value) && value.length === 0) return;
        compact[key] = value;
      });
      return compact;
    },
    // The value the `filters` query param should hold for a filters object; undefined drops the param.
    filtersQuery(filters) {
      const compact = this.pruneFilters(filters);
      return Object.keys(compact).length ? JSON.stringify(compact) : undefined;
    },
    updateRouteHash(hash) {
      this.$router.replace({
        query: {...this.$router.currentRoute.query, filters: this.filtersQuery(this.filters)},
        hash,
      });
    },
    handleFiltersQuery() {
      const desired = this.filtersQuery(this.filters);
      if ((this.$route.query.filters || undefined) !== desired) {
        this.$router.replace({...this.$route, query: {...this.$route.query, filters: desired}});
      }
    },
    clearFiltersQuery() {
      if (this.$route.query.filters) {
        this.$router.replace({...this.$route, query: {...this.$route.query, filters: undefined}});
      }
    },
  },
};
